// Admin › OTA Releases › Register updates › one version, one change at a time.
// Takes the whole window: the version's rows on the left, by merge, the change
// in the middle, and what to check on the Scout with the decision on the right.
// Rows whose paths changed the same way are one entry (a bundle), checked together.
// Keys: A accept, R reject, S skip, ← → previous and next.
// docs/core/operations/REGISTER_UPDATES.md

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, PencilIcon } from '@heroicons/react/24/outline';
import {
  FACT_COLUMNS,
  isDone,
  needsCheck,
  nzDay,
  plainTitle,
  trackChanges,
  useDecide,
  useDecideMany,
  useRegisterRelease,
  type RegisterChange,
  type RegisterDecision,
  type RegisterMerge,
  type RegisterRelease,
} from './data/register';
import { byMerge, byScreen, inOrder, itemDone, itemNeedsCheck, itemsOf, Kbd, KindPill, ProgressSegments, StatusGlyph, STATUS_WORDS, type Item } from './register/parts';
import { AppIcon } from './changelog/AppIcon';

const COMMIT_URL = 'https://github.com/vortex-ms/vortexnav-client/commit/';

export function RegisterReviewPage() {
  const { releaseId } = useParams();
  const [params, setParams] = useSearchParams();
  const detail = useRegisterRelease(releaseId);
  const merges = useMemo(() => detail.data?.merges ?? [], [detail.data]);
  const changes = useMemo(() => inOrder(detail.data?.changes ?? [], merges), [detail.data, merges]);
  const release = detail.data?.release;
  const rail = merges.length
    ? byMerge(changes, merges).filter((g) => g.changes.length > 0).map((g) => ({
      key: g.merge?.id ?? 'release',
      title: g.merge ? plainTitle(g.merge.title) : 'Drafted with the whole release',
      sub: g.merge ? `${g.merge.pr_number ? `#${g.merge.pr_number} · ` : ''}${g.merge.merged_at ? `merged ${nzDay(g.merge.merged_at)}` : ''}` : '',
      items: itemsOf(g.changes),
    }))
    : byScreen(changes).map((g) => ({ key: g.screen, title: g.screen, sub: '', items: itemsOf(g.changes) }));
  const items = rail.flatMap((g) => g.items);

  // A bundle by its key; a change on its own; else the next thing to check.
  const asked = params.get('bundle') ? `b:${params.get('bundle')}` : params.get('change');
  const current: Item | undefined =
    items.find((i) => i.id === asked)
    ?? (asked && !params.get('bundle') ? (() => { const c = changes.find((x) => x.id === asked); return c ? { kind: 'change' as const, id: c.id, change: c, changes: [c] } : undefined; })() : undefined)
    ?? items.find((i) => i.changes.some((c) => c.status === 'conflict'))
    ?? items.find(itemNeedsCheck)
    ?? items[0];
  const at = current ? items.findIndex((i) => i.id === current.id) : -1;
  const go = (i: Item | undefined) => { if (i) setParams(i.kind === 'bundle' ? { bundle: i.key } : { change: i.id }); };
  /** The next entry still to check after this one, wrapping round; else simply the next. */
  const nextToCheck = () => {
    const after = [...items.slice(at + 1), ...items.slice(0, Math.max(at, 0))];
    return after.find(itemNeedsCheck) ?? items[at + 1];
  };
  const back = `/admin/releases/register?release=${releaseId ?? ''}`;
  const done = changes.filter(isDone).length;

  return (
    <div className="admin-console flex min-h-screen flex-col bg-slate-900 text-slate-300">
      <header className="sticky top-0 z-30 flex h-16 flex-none items-center gap-4 border-b border-slate-400/15 bg-slate-900/90 px-3 backdrop-blur-md sm:px-5">
        <Link to={back} className="inline-flex min-h-[44px] items-center gap-2.5 rounded-lg pr-2 text-sm font-semibold text-slate-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
          <ChevronLeftIcon className="h-[18px] w-[18px]" aria-hidden="true" />
          {release && <AppIcon app={release.app} size={26} />}
          <span className="hidden sm:inline">{release ? `${release.module} ${release.version}` : 'Register updates'}</span>
          {release && !release.released && <span className="hidden font-medium text-cyan-300 md:inline">not released yet</span>}
        </Link>
        {changes.length > 0 && <ProgressSegments changes={changes} currentId={current?.kind === 'change' ? current.id : undefined} className="max-w-[520px] flex-1" />}
        {changes.length > 0 && (
          <span className="flex-none text-[13px] text-slate-400"><span className="font-semibold text-white">{done}</span> of {changes.length} done</span>
        )}
        <div className="ml-auto flex flex-none gap-2">
          <IconButton label="Previous change" disabled={at <= 0} onClick={() => go(items[at - 1])}><ChevronLeftIcon className="h-4 w-4" /></IconButton>
          <IconButton label="Next change" disabled={at < 0 || at >= items.length - 1} onClick={() => go(items[at + 1])}><ChevronRightIcon className="h-4 w-4" /></IconButton>
        </div>
      </header>

      {detail.isLoading && <p className="p-8 text-[15px] text-slate-400">Loading…</p>}
      {detail.isError && <p className="p-8 text-[15px] text-red-400">Couldn't load this release. <Link to={back} className="text-cyan-300">Back to Register updates</Link></p>}
      {detail.isSuccess && !current && <p className="p-8 text-[15px] text-slate-400">This release has no changes to the register.</p>}

      {release && current && (
        <div className="grid flex-1 lg:grid-cols-[288px_minmax(0,1fr)_368px]">
          <nav aria-label="Changes in this release" className="hidden border-r border-slate-400/15 px-2.5 py-4 lg:block">
            {rail.map((g) => (
              <div key={g.key}>
                <div className="flex flex-col px-2.5 pb-1 pt-3">
                  <span className={`text-[13px] font-semibold ${g.sub ? 'text-slate-300' : 'text-slate-500'}`}>{g.title}</span>
                  {g.sub && <span className="text-[13px] text-slate-500">{g.sub}</span>}
                </div>
                {g.items.map((i) => {
                  const on = i.id === current.id;
                  if (i.kind === 'bundle') {
                    const done = itemDone(i);
                    return (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => go(i)}
                        aria-current={on ? 'true' : undefined}
                        className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${on ? 'bg-cyan-400/10' : 'hover:bg-slate-400/[0.06]'}`}
                      >
                        <span aria-hidden="true" className={`inline-flex h-4 w-4 flex-none items-center justify-center rounded-[4px] text-[13px] font-bold ${done ? 'bg-emerald-400/15 text-emerald-400' : on ? 'shadow-[inset_0_0_0_2px_#22d3ee]' : 'shadow-[inset_0_0_0_1.5px_#475569]'}`}>{done ? '✓' : ''}</span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className={`truncate text-sm ${on ? 'font-semibold text-white' : done ? 'text-slate-500' : 'text-slate-300'}`}>{i.title}</span>
                          <span className="text-[13px] text-slate-500">{i.changes.length} rows, one change</span>
                        </span>
                      </button>
                    );
                  }
                  const c = i.change;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => go(i)}
                      aria-current={on ? 'true' : undefined}
                      className={`flex min-h-[36px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${on ? 'bg-cyan-400/10' : 'hover:bg-slate-400/[0.06]'}`}
                    >
                      <StatusGlyph status={c.status} current={on} size={16} />
                      <span className={`min-w-0 flex-1 truncate text-sm ${on ? 'font-semibold text-white' : isDone(c) ? 'text-slate-500' : 'text-slate-300'}`}>{c.element}</span>
                      <span className="sr-only">{STATUS_WORDS[c.status]}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
          {current.kind === 'bundle'
            ? <BundleReview key={current.id} bundle={current} release={release} merges={merges} onNext={() => go(nextToCheck())} onPrev={() => go(items[at - 1])} onOpen={(c) => setParams({ change: c.id })} />
            : <ChangeReview key={current.id} change={current.change} release={release} merges={merges} onNext={() => go(nextToCheck())} onPrev={() => go(items[at - 1])} />}
        </div>
      )}
    </div>
  );
}

const pathSteps = (t: string) => t.split(' → ').map((x) => x.trim()).filter(Boolean);
const newPath = (c: RegisterChange) => c.changes.find((x) => x.column === 'Navigation Path')?.proposed ?? '';

/**
 * A bundle: rows whose Navigation Path changed the same way, accepted or
 * rejected together. Unticked rows are left open, to check on their own.
 */
function BundleReview({ bundle, release, merges, onNext, onPrev, onOpen }: {
  bundle: Extract<Item, { kind: 'bundle' }>; release: RegisterRelease; merges: RegisterMerge[];
  onNext: () => void; onPrev: () => void; onOpen: (c: RegisterChange) => void;
}) {
  const decide = useDecideMany(release.id);
  const open = bundle.changes.filter(needsCheck);
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(open.map((c) => c.id)));
  const [done, setDone] = useState<Set<string>>(new Set());
  const chosen = open.filter((c) => ticked.has(c.id));

  // The shared change: the steps before it, what goes, what comes.
  const first = bundle.changes[0];
  const a = pathSteps(first.current['Navigation Path'] ?? ''), b = pathSteps(newPath(first));
  let p = 0;
  while (p < a.length && p < b.length && a[p].toLowerCase() === b[p].toLowerCase()) p++;
  let q = 0;
  while (q < a.length - p && q < b.length - p && a[a.length - 1 - q].toLowerCase() === b[b.length - 1 - q].toLowerCase()) q++;
  const lead = a.slice(0, p), gone = a.slice(p, a.length - q), come = b.slice(p, b.length - q);
  const newest = merges.filter((m) => bundle.changes.some((c) => c.merge_id === m.id))
    .reduce<string | null>((t, m) => (m.merged_at && (!t || m.merged_at > t) ? m.merged_at : t), null);

  const send = (action: 'accept' | 'reject') => {
    if (!chosen.length || decide.isPending) return;
    decide.mutate({ ids: chosen.map((c) => c.id), action }, { onSuccess: (r) => { if (!r.failed.length) onNext(); } });
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || (t && t.closest('input, textarea, select, [contenteditable]'))) return;
      const k = e.key.toLowerCase();
      if (k === 'a') { e.preventDefault(); send('accept'); }
      else if (k === 'r') { e.preventDefault(); send('reject'); }
      else if (k === 's' || e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); onPrev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const failed = decide.data?.failed ?? [];
  const error = decide.error ? "Couldn't save that. Try again." : failed.length ? `${failed.length} could not be decided: ${failed[0].error}` : null;
  const toggle = (set: Set<string>, id: string) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };

  const actions = (
    <div className="flex flex-col gap-2.5">
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
      {open.length === 0 ? (
        <p className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3 text-sm text-emerald-100">Every row here is decided.</p>
      ) : (
        <>
          <button type="button" onClick={() => send('accept')} disabled={!chosen.length || decide.isPending} className="flex min-h-[48px] items-center justify-center gap-2.5 rounded-[10px] bg-cyan-500 px-4 text-[15px] font-semibold text-white shadow-sm shadow-cyan-500/20 hover:bg-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-50">
            Accept {chosen.length} path{chosen.length === 1 ? '' : 's'} <span className="hidden sm:inline-flex"><Kbd onAccent>A</Kbd></span>
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            <button type="button" onClick={() => send('reject')} disabled={!chosen.length || decide.isPending} className="flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border border-rose-400/30 bg-rose-400/[0.08] px-3 text-sm font-semibold text-rose-300 hover:bg-rose-400/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 disabled:opacity-50">
              Reject {chosen.length === open.length ? 'all' : chosen.length} <span className="hidden sm:inline-flex"><Kbd>R</Kbd></span>
            </button>
            <button type="button" onClick={onNext} className="flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border border-slate-400/20 px-3 text-sm font-semibold text-slate-300 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
              Skip <span className="hidden sm:inline-flex"><Kbd>S</Kbd></span>
            </button>
          </div>
          <p className="text-center text-[13px] leading-snug text-slate-500">Into the register within the hour, verified on {release.version}.</p>
        </>
      )}
    </div>
  );

  const scout = (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-white">On the Scout</h2>
        <span className="rounded-full bg-slate-400/10 px-2.5 py-0.5 text-[13px] text-slate-300">{release.released ? `Running ${release.version}` : 'A main build'}</span>
      </div>
      {!release.released && newest && <p className="text-sm text-slate-400">Needs a Scout with main from {nzDay(newest)} or later.</p>}
      <fieldset className="space-y-3">
        <legend className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-slate-500">Do</legend>
        {first.check_steps.map((t) => (
          <label key={t} className={`flex cursor-pointer gap-3 text-sm leading-relaxed ${done.has(t) ? 'text-slate-400' : 'text-slate-200'}`}>
            <input type="checkbox" checked={done.has(t)} onChange={() => setDone((d) => toggle(d, t))} className="mt-0.5 h-[18px] w-[18px] flex-none accent-cyan-400 [color-scheme:dark]" />
            <span>{t}</span>
          </label>
        ))}
        <p className="text-sm leading-relaxed text-slate-400">Check the rows listed are where the new path says.</p>
      </fieldset>
    </div>
  );

  return (
    <>
      <main className="min-w-0 space-y-6 px-4 py-6 sm:px-8 lg:px-10 lg:py-8">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="rounded-full bg-cyan-400/10 px-2.5 py-0.5 text-[13px] font-semibold text-cyan-300">Path change</span>
            <span className="text-sm text-slate-400">{bundle.changes.length} rows, checked together</span>
          </div>
          <h1 className="text-[26px] font-bold tracking-tight text-white sm:text-[30px]">{bundle.title}</h1>
          <p className="text-base leading-relaxed text-slate-200">
            {lead.length > 0 && <>{lead.join(' → ')} → </>}
            {gone.length > 0 && <del className="text-red-400 decoration-red-400/70">{gone.join(' → ')}</del>}{' '}
            {come.length > 0 && <ins className="rounded-[3px] bg-emerald-400/15 px-[3px] py-px text-emerald-200 no-underline">{come.join(' → ')}</ins>}
            {q > 0 && ' → …'}
          </p>
          <p className="text-sm text-slate-400">Only the path changes for these rows. Rows whose wording also changes are checked on their own.</p>
        </div>

        <div className="lg:hidden"><details className="rounded-2xl border border-slate-400/20 bg-slate-800/60"><summary className="flex min-h-[52px] cursor-pointer list-none items-center px-4 text-[15px] font-semibold text-slate-200">On the Scout</summary><div className="border-t border-slate-400/15 px-4 py-5">{scout}</div></details></div>

        <ul className="border-t border-slate-400/15">
          {bundle.changes.map((c) => {
            const mine = pathSteps(newPath(c));
            const tail = mine.slice(p);
            const isOpen = needsCheck(c);
            return (
              <li key={c.id} className="grid grid-cols-[28px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-b border-slate-400/10 py-3 sm:grid-cols-[28px_220px_minmax(0,1fr)_auto]">
                {isOpen
                  ? <input type="checkbox" aria-label={`Include ${c.element}`} checked={ticked.has(c.id)} onChange={() => setTicked((t) => toggle(t, c.id))} className="h-[18px] w-[18px] accent-cyan-400 [color-scheme:dark]" />
                  : <StatusGlyph status={c.status} size={18} />}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-[15px] font-semibold text-slate-100">{c.element}</span>
                  {c.doc_id && <span className="font-mono text-[13px] text-slate-500">{c.doc_id}</span>}
                </span>
                <span className="col-start-2 text-sm text-slate-400 sm:col-start-auto">… → <span className="text-emerald-200">{tail.join(' → ')}</span></span>
                {isOpen && <button type="button" onClick={() => onOpen(c)} className="col-start-2 justify-self-start text-[13px] text-cyan-300 hover:text-white sm:col-start-auto">On its own</button>}
              </li>
            );
          })}
        </ul>
        {open.length > 0 && <p className="text-sm text-slate-500">Untick a row to leave it out; it stays to check on its own.</p>}
      </main>

      <aside className="hidden border-l border-slate-400/15 bg-slate-800/35 lg:flex lg:flex-col">
        <div className="sticky top-16 flex max-h-[calc(100vh-4rem)] flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-7">{scout}</div>
          <div className="border-t border-slate-400/15 px-6 pb-6 pt-4">{actions}</div>
        </div>
      </aside>

      <div className="sticky bottom-0 z-20 border-t border-slate-400/15 bg-slate-900/95 px-4 pb-5 pt-3 backdrop-blur-md lg:hidden">{actions}</div>
    </>
  );
}

function IconButton({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-400/20 text-slate-300 hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-40 sm:h-9 sm:w-9"
    >
      {children}
    </button>
  );
}

interface FieldState { use: boolean; text: string; editing: boolean }

/** One change: what it does to the row, and the call on it. Keyed by change, so its state starts fresh. */
function ChangeReview({ change, release, merges, onNext, onPrev }: { change: RegisterChange; release: RegisterRelease; merges: RegisterMerge[]; onNext: () => void; onPrev: () => void }) {
  const decide = useDecide(release.id);
  const decided = change.status === 'accepted' || change.status === 'rejected' || change.status === 'applied';
  const [fields, setFields] = useState<Record<string, FieldState>>(() =>
    Object.fromEntries(change.changes.map((c) => [c.column, {
      use: decided && change.decision ? c.column in change.decision.columns : true,
      text: change.decision?.columns[c.column] ?? c.proposed,
      editing: false,
    }])));
  const [view, setView] = useState<'track' | 'both'>('track');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState(change.decision?.note ?? '');
  const [showSame, setShowSame] = useState(false);

  const used = Object.entries(fields).filter(([, f]) => f.use);
  const conflict = change.status === 'conflict';
  const canAccept = !decided && (change.kind === 'retire' || used.length > 0) && !decide.isPending;
  const acceptLabel = conflict ? 'Use the proposal'
    : change.kind === 'retire' ? 'Retire the row'
      : change.kind === 'new' ? 'Add the row'
        : `Accept ${used.length} change${used.length === 1 ? '' : 's'}`;
  const rejectLabel = conflict ? "Keep the sheet's text" : change.kind === 'retire' ? "It's still there" : change.kind === 'new' ? 'Not needed' : 'Reject';

  const send = (body: RegisterDecision) => decide.mutate({ id: change.id, body }, { onSuccess: (c) => { if (c.status !== 'open' && c.status !== 'conflict') onNext(); } });
  const accept = () => {
    if (!canAccept) return;
    const columns = change.kind === 'retire' ? {} : Object.fromEntries(used.map(([k, f]) => [k, f.text]));
    send({ action: 'accept', columns, note: note.trim() || null });
  };
  const reject = () => { if (!decided && !decide.isPending) send({ action: 'reject', note: note.trim() || null }); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || (t && t.closest('input, textarea, select, [contenteditable]'))) return;
      const k = e.key.toLowerCase();
      if (k === 'a') { e.preventDefault(); accept(); }
      else if (k === 'r') { e.preventDefault(); reject(); }
      else if (k === 's' || e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); onPrev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const set = (col: string, patch: Partial<FieldState>) => setFields((f) => ({ ...f, [col]: { ...f[col], ...patch } }));
  const steps = [...change.check_steps.map((t) => ({ t, look: false })), ...change.look_for.map((t) => ({ t, look: true }))];
  const toggle = (t: string) => setChecked((s) => { const n = new Set(s); if (n.has(t)) n.delete(t); else n.add(t); return n; });
  const changedCols = new Set(change.changes.map((c) => c.column));
  const same = FACT_COLUMNS.filter((c) => !changedCols.has(c) && change.current[c]);
  const proposedElement = change.changes.find((c) => c.column === 'Element');
  const title = change.kind === 'update' && proposedElement && fields.Element?.use ? fields.Element.text : change.element;
  // The merges behind this change: its own, then any earlier one whose draft of the row it folds in.
  const prs = new Set(change.commits.map((c) => c.pr).filter(Boolean));
  const own = merges.find((m) => m.id === change.merge_id);
  const by = [...(own ? [own] : []), ...merges.filter((m) => m !== own && m.pr_number && prs.has(m.pr_number))];
  const newest = by.reduce<string | null>((t, m) => (m.merged_at && (!t || m.merged_at > t) ? m.merged_at : t), null);
  const error = (decide.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ?? (decide.error ? "Couldn't save that. Try again." : null);

  const actions = (
    <div className="flex flex-col gap-2.5">
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
      {decided ? (
        <DecidedNote change={change} onReopen={change.status === 'applied' ? undefined : () => send({ action: 'reopen' })} busy={decide.isPending} />
      ) : (
        <>
          <button type="button" onClick={accept} disabled={!canAccept} className="flex min-h-[48px] items-center justify-center gap-2.5 rounded-[10px] bg-cyan-500 px-4 text-[15px] font-semibold text-white shadow-sm shadow-cyan-500/20 hover:bg-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-50">
            {acceptLabel} <span className="hidden sm:inline-flex"><Kbd onAccent>A</Kbd></span>
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            <button type="button" onClick={reject} disabled={decide.isPending} className="flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border border-rose-400/30 bg-rose-400/[0.08] px-3 text-sm font-semibold text-rose-300 hover:bg-rose-400/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 disabled:opacity-50">
              {rejectLabel} <span className="hidden sm:inline-flex"><Kbd>R</Kbd></span>
            </button>
            <button type="button" onClick={onNext} className="flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border border-slate-400/20 px-3 text-sm font-semibold text-slate-300 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
              Skip <span className="hidden sm:inline-flex"><Kbd>S</Kbd></span>
            </button>
          </div>
          <p className="text-center text-[13px] leading-snug text-slate-500">Into the register within the hour, verified on {release.version}.</p>
        </>
      )}
    </div>
  );

  return (
    <>
      <main className="min-w-0 space-y-6 px-4 py-6 sm:px-8 lg:px-10 lg:py-8">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <KindPill kind={change.kind} conflict={conflict} />
            {change.doc_id && <span className="font-mono text-[13px] text-slate-500">{change.doc_id}</span>}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
            <h1 className="text-[26px] font-bold tracking-tight text-white sm:text-[30px]">{title}</h1>
            {title !== change.element && <span className="text-[15px] text-slate-500 line-through">{change.element}</span>}
          </div>
          <PathChips change={change} fields={fields} />
        </div>

        {by.length > 0 && (
          <div className="space-y-1.5 rounded-xl border border-slate-400/15 bg-slate-800/50 px-4 py-3">
            <div className="text-[13px] font-semibold text-slate-400">Changed by</div>
            {by.map((m, i) => (
              <p key={m.id} className="text-sm leading-relaxed text-slate-200">
                {m.pr_number && m.url
                  ? <a href={m.url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-white">#{m.pr_number}</a>
                  : m.pr_number ? `#${m.pr_number}` : null}{' '}
                {plainTitle(m.title)} · {m.author.split(' ')[0]}{m.merged_at ? ` · merged ${nzDay(m.merged_at)}` : ''}
                {i > 0 && <span className="text-slate-400"> (its draft of this row is folded in here)</span>}
              </p>
            ))}
          </div>
        )}

        <div className="lg:hidden">{scoutPanel()}</div>

        {conflict && (
          <p className="rounded-xl border border-amber-400/25 bg-amber-400/[0.08] px-4 py-3 text-[15px] leading-relaxed text-amber-100">
            This row was changed in the sheet after it was drafted, so nothing was written. Keep the sheet's text, or use the proposal.
          </p>
        )}

        {change.kind === 'retire' && (
          <div className="space-y-1.5 rounded-2xl bg-slate-800/45 px-5 py-4">
            <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">The row says now</div>
            <p className="text-base leading-relaxed text-slate-200">{change.current['What it does'] || change.current.Element || change.element}</p>
            <p className="pt-1 text-[13px] text-slate-500">Retiring moves it to the Retired tab as retired in {release.version}. Nothing is deleted.</p>
          </div>
        )}

        {change.kind === 'new' && (
          <div className="space-y-1">
            <p className="text-[13px] text-slate-500">
              Goes in {change.anchor_doc_id ? <>after <span className="font-mono">{change.anchor_doc_id}</span></> : 'at the end of its section'} on the {change.sheet_tab} tab, with the next free number.
            </p>
            <dl className="grid gap-x-5 sm:grid-cols-[160px_minmax(0,1fr)]">
              {change.changes.map((c) => (
                <div key={c.column} className="contents">
                  <dt className="pt-5 text-[13px] font-semibold text-slate-400 sm:border-b sm:border-slate-400/10 sm:pb-3">{c.column}</dt>
                  <dd className="border-b border-slate-400/10 py-2.5">
                    <textarea
                      aria-label={c.column}
                      rows={c.column === 'What it does' || c.column === 'How it is used' ? 2 : 1}
                      value={fields[c.column]?.text ?? ''}
                      disabled={decided}
                      onChange={(e) => set(c.column, { text: e.target.value })}
                      className="w-full resize-y rounded-[10px] border border-slate-400/20 bg-slate-950/60 px-3 py-2 text-[15px] leading-relaxed text-slate-100 focus:border-cyan-400 focus:outline-none focus:ring-[3px] focus:ring-cyan-400/20 disabled:opacity-70"
                    />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {change.kind === 'update' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div role="tablist" aria-label="Show the changes" className="flex gap-0.5 rounded-[10px] bg-slate-400/10 p-[3px]">
                {([['track', 'Track changes'], ['both', 'Before and after']] as const).map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)}
                    className={`min-h-[30px] rounded-[7px] px-3 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${view === k ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <span className="text-[13px] text-slate-500">{change.changes.length} of {change.changes.length + same.length} fields change</span>
            </div>

            <div className="border-t border-slate-400/15">
              {change.changes.map((c) => {
                const f = fields[c.column];
                const before = change.current[c.column] ?? '';
                const sheet = change.sheet_now?.[c.column];
                return (
                  <div key={c.column} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-5 gap-y-2 border-b border-slate-400/15 py-4 sm:grid-cols-[160px_minmax(0,1fr)_auto]">
                    <div className="order-1 self-center text-[13px] font-semibold text-slate-400 sm:self-start sm:pt-0.5">{c.column}</div>
                    <div className="order-3 col-span-2 min-w-0 space-y-2 sm:order-2 sm:col-span-1">
                      {conflict && sheet !== undefined && sheet !== before && (
                        <div className="text-sm leading-relaxed"><span className="font-semibold text-amber-300">In the sheet now: </span><span className="text-slate-300">{sheet || '(empty)'}</span></div>
                      )}
                      {f.editing ? (
                        <>
                          <textarea
                            aria-label={c.column}
                            autoFocus
                            rows={Math.min(6, Math.max(2, Math.ceil(f.text.length / 70)))}
                            value={f.text}
                            onChange={(e) => set(c.column, { text: e.target.value })}
                            className="w-full resize-y rounded-[10px] border border-cyan-400 bg-slate-950/60 px-3 py-2.5 text-[15px] leading-relaxed text-white shadow-[0_0_0_3px_rgba(34,211,238,0.18)] focus:outline-none"
                          />
                          <div className="flex flex-wrap gap-x-4 text-[13px]">
                            <span className="text-slate-400">Your wording goes in exactly as written.</span>
                            {f.text !== c.proposed && <button type="button" onClick={() => set(c.column, { text: c.proposed })} className="text-cyan-300 hover:text-white">Back to the proposal</button>}
                          </div>
                        </>
                      ) : f.use ? (
                        <Changed before={before} after={f.text} view={view} byStep={c.column === 'Navigation Path'} />
                      ) : (
                        <>
                          <p className="text-[15px] leading-relaxed text-slate-400">{before || '(empty)'}</p>
                          <p className="text-[13px] text-slate-500">Keeping the current text. Turn it on to use: <span className="text-slate-300">{f.text}</span></p>
                        </>
                      )}
                      <p className="text-[13px] leading-relaxed text-slate-500">{c.reason}</p>
                    </div>
                    {!decided && (
                      <div className="order-2 flex items-start gap-1.5 sm:order-3">
                        <button
                          type="button"
                          aria-label={f.editing ? `Done editing ${c.column}` : `Edit ${c.column}`}
                          aria-pressed={f.editing}
                          onClick={() => set(c.column, { editing: !f.editing, use: true })}
                          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${f.editing ? 'border-cyan-400 text-cyan-300' : 'border-slate-400/20 text-slate-300 hover:bg-slate-800'}`}
                        >
                          <PencilIcon className="h-[15px] w-[15px]" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={f.use}
                          aria-label={`Use the new ${c.column}`}
                          onClick={() => set(c.column, { use: !f.use, editing: false })}
                          className={`mt-1.5 flex h-6 w-10 flex-none rounded-full p-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${f.use ? 'justify-end bg-cyan-500' : 'justify-start bg-slate-700'}`}
                        >
                          <span className="h-5 w-5 rounded-full bg-white" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {same.length > 0 && (
                <div className="py-3">
                  <button type="button" aria-expanded={showSame} onClick={() => setShowSame((v) => !v)} className="flex min-h-[36px] items-center gap-2 text-left text-[13px] text-slate-500 hover:text-slate-300">
                    <ChevronDownIcon className={`h-3.5 w-3.5 flex-none transition-transform ${showSame ? '' : '-rotate-90'}`} aria-hidden="true" />
                    {same.length} field{same.length === 1 ? ' stays' : 's stay'} the same: {same.join(', ')}
                  </button>
                  {showSame && (
                    <dl className="mt-2 grid gap-x-5 gap-y-3 sm:grid-cols-[160px_minmax(0,1fr)]">
                      {same.map((c) => (
                        <div key={c} className="contents">
                          <dt className="text-[13px] font-semibold text-slate-500">{c}</dt>
                          <dd className="text-sm leading-relaxed text-slate-400">{change.current[c]}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {change.commits.length > 0 && (
          <ul className="space-y-2">
            {change.commits.map((c) => (
              <li key={c.sha} className="flex items-center gap-2.5 text-[13px] text-slate-400">
                <span aria-hidden="true" className="inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-cyan-700 text-[13px] font-bold text-white">{(c.author || '?').charAt(0).toUpperCase()}</span>
                <span className="flex-none text-slate-300">{c.author}</span>
                <a href={`${COMMIT_URL}${c.sha}`} target="_blank" rel="noreferrer" className="flex-none font-mono text-slate-500 hover:text-cyan-300">{c.sha.slice(0, 8)}</a>
                <span className="truncate">{c.title}</span>
              </li>
            ))}
          </ul>
        )}

      </main>

      <aside className="hidden border-l border-slate-400/15 bg-slate-800/35 lg:flex lg:flex-col">
        <div className="sticky top-16 flex max-h-[calc(100vh-4rem)] flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-7">{scoutBody()}</div>
          <div className="border-t border-slate-400/15 px-6 pb-6 pt-4">{actions}</div>
        </div>
      </aside>

      <div className="sticky bottom-0 z-20 border-t border-slate-400/15 bg-slate-900/95 px-4 pb-5 pt-3 backdrop-blur-md lg:hidden">{actions}</div>
    </>
  );

  function scoutBody() {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-white">On the Scout</h2>
          <span className="rounded-full bg-slate-400/10 px-2.5 py-0.5 text-[13px] text-slate-300">{release.released ? `Running ${release.version}` : 'A main build'}</span>
        </div>
        {!release.released && newest && (
          <p className="text-sm text-slate-400">Needs a Scout with main from {nzDay(newest)} or later.</p>
        )}
        {(['do', 'look'] as const).map((part) => {
          const items = steps.filter((s) => s.look === (part === 'look'));
          if (items.length === 0) return null;
          return (
            <fieldset key={part} className="space-y-3">
              <legend className={`mb-3 text-[13px] font-semibold uppercase tracking-wider ${part === 'look' ? 'text-amber-400' : 'text-slate-500'}`}>
                {part === 'look' ? "Look for, the code can't show it" : 'Do'}
              </legend>
              {items.map((s) => (
                <label key={s.t} className={`flex cursor-pointer gap-3 text-sm leading-relaxed ${checked.has(s.t) ? 'text-slate-400' : 'text-slate-200'}`}>
                  <input type="checkbox" checked={checked.has(s.t)} onChange={() => toggle(s.t)} className="mt-0.5 h-[18px] w-[18px] flex-none accent-cyan-400 [color-scheme:dark]" />
                  <span>{s.t}</span>
                </label>
              ))}
            </fieldset>
          );
        })}
        <label className="block space-y-2">
          <span className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">Note, optional</span>
          <textarea
            rows={3}
            value={note}
            disabled={decided}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Saved to the row's Internal Comments"
            className="w-full resize-none rounded-[10px] border border-slate-400/20 bg-slate-950/60 px-3 py-2.5 text-sm leading-relaxed text-slate-200 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-[3px] focus:ring-cyan-400/20 disabled:opacity-70"
          />
        </label>
      </div>
    );
  }

  function scoutPanel() {
    const n = steps.length;
    return (
      <details className="rounded-2xl border border-slate-400/20 bg-slate-800/60">
        <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-3 px-4 text-[15px] font-semibold text-slate-200">
          <span className="flex-1">On the Scout</span>
          {n > 0 && <span className="font-medium text-slate-400">{[...checked].length} of {n} checked</span>}
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        </summary>
        <div className="border-t border-slate-400/15 px-4 py-5">{scoutBody()}</div>
      </details>
    );
  }
}

/** A changed field: track changes, or old struck over new. A rewrite always shows old over new. */
function Changed({ before, after, view, byStep }: { before: string; after: string; view: 'track' | 'both'; byStep: boolean }) {
  const parts = view === 'track' && before ? trackChanges(before, after, byStep) : null;
  if (parts) {
    return (
      <p className="text-[15px] leading-[1.7] text-slate-200">
        {parts.map((p, i) => p.kind === 'same' ? <span key={i}>{p.text}</span>
          : p.kind === 'removed' ? <del key={i} className="text-red-400 decoration-red-400/70">{p.text}{byStep ? '' : ' '}</del>
            : <ins key={i} className="rounded-[3px] bg-emerald-400/15 px-[3px] py-px text-emerald-200 no-underline">{p.text}</ins>)}
      </p>
    );
  }
  return (
    <div className="space-y-1.5">
      {before && <p className="text-sm leading-relaxed text-red-400 line-through decoration-red-400/60"><span className="sr-only">Was: </span>{before}</p>}
      <p className="text-[15px] leading-[1.7] text-emerald-200"><span className="sr-only">Now: </span><span className="rounded bg-emerald-400/[0.12] px-1 py-px [box-decoration-break:clone]">{after}</span></p>
    </div>
  );
}

/** The navigation path as steps, removed steps struck and added ones lit, when the path changes. */
function PathChips({ change, fields }: { change: RegisterChange; fields: Record<string, FieldState> }) {
  const now = change.current['Navigation Path'] ?? '';
  const f = fields['Navigation Path'];
  const next = f?.use ? f.text : now;
  if (!now && !next) return null;
  const parts = next !== now ? trackChanges(now, next, true) : null;
  const steps = parts
    ? parts.flatMap((p) => p.text.split(' → ').map((t) => ({ t: t.trim(), kind: p.kind })).filter((s) => s.t))
    : next.split(' → ').map((t) => ({ t: t.trim(), kind: 'same' as const }));
  return (
    <ol className="flex flex-wrap items-center gap-1.5 text-[13px]" aria-label="Navigation path">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-1.5">
          <span className={`rounded-md px-2 py-0.5 ${s.kind === 'added' ? 'bg-emerald-400/15 text-emerald-200' : s.kind === 'removed' ? 'text-red-400 line-through' : 'bg-slate-400/10 text-slate-300'}`}>{s.t}</span>
          {i < steps.length - 1 && <span aria-hidden="true" className="text-slate-600">›</span>}
        </li>
      ))}
    </ol>
  );
}

function DecidedNote({ change, onReopen, busy }: { change: RegisterChange; onReopen?: () => void; busy: boolean }) {
  const who = change.decided_by_name ? ` by ${change.decided_by_name}` : '';
  const when = change.decided_at ? ` on ${nzDay(change.decided_at)}` : '';
  const line = change.status === 'applied' ? `In the register since ${nzDay(change.applied_at)}. Accepted${who}${when}.`
    : change.status === 'accepted' ? `Accepted${who}${when}. Goes into the register within the hour.`
      : `Kept as it was${who}${when}.`;
  return (
    <div className="space-y-3 rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3">
      <p className="text-sm leading-relaxed text-emerald-100">{line}</p>
      {change.decision?.note && <p className="text-sm leading-relaxed text-slate-300">Note: {change.decision.note}</p>}
      {onReopen && (
        <button type="button" onClick={onReopen} disabled={busy} className="min-h-[36px] rounded-lg border border-slate-400/20 px-3 text-sm font-semibold text-slate-200 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-50">
          Change my mind
        </button>
      )}
    </div>
  );
}
